import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const CapacitySection = ({ formData, errors, onChange, onCheckboxChange }) => {
  const calculateDeadline = () => {
    if (!formData?.date) return '';
    const eventDate = new Date(formData.date);
    const deadline = new Date(eventDate);
    deadline?.setDate(deadline?.getDate() - 1);
    return deadline?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Convert stored YYYY-MM-DD to display GG.AA.YYYY
  const toDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr?.split('-');
    if (parts?.length !== 3) return dateStr;
    const [year, month, day] = parts;
    return `${day}.${month}.${year}`;
  };

  // Convert display GG.AA.YYYY to stored YYYY-MM-DD
  const toStoredDate = (displayStr) => {
    const parts = displayStr?.split('.');
    if (parts?.length !== 3 || parts?.[2]?.length !== 4) return '';
    const [day, month, year] = parts;
    return `${year}-${month?.padStart(2, '0')}-${day?.padStart(2, '0')}`;
  };

  const handleDeadlineInput = (e) => {
    let raw = e?.target?.value?.replace(/[^0-9]/g, '');
    let formatted = '';
    if (raw?.length <= 2) {
      formatted = raw;
    } else if (raw?.length <= 4) {
      formatted = raw?.slice(0, 2) + '.' + raw?.slice(2);
    } else {
      formatted = raw?.slice(0, 2) + '.' + raw?.slice(2, 4) + '.' + raw?.slice(4, 8);
    }
    // Update display value via a synthetic event
    const parts = formatted?.split('.');
    let storedValue = formatted; // keep raw display until complete
    if (parts?.length === 3 && parts?.[2]?.length === 4) {
      storedValue = toStoredDate(formatted);
    } else {
      storedValue = '';
    }
    // Store the display string temporarily in a display field, and stored in registrationDeadline
    onChange({ target: { name: 'registrationDeadlineDisplay', value: formatted } });
    if (storedValue) {
      onChange({ target: { name: 'registrationDeadline', value: storedValue } });
    }
  };

  const displayValue = formData?.registrationDeadlineDisplay !== undefined
    ? formData?.registrationDeadlineDisplay
    : toDisplayDate(formData?.registrationDeadline || '');

  return (
    <div className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl p-4 md:p-6 lg:p-8 shadow-organic">
      <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-5 lg:mb-6">
        Kapasite & Kayıt
      </h2>
      <div className="space-y-6 md:space-y-7 lg:space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
          <Input
            label="Maksimum Katılımcı"
            type="number"
            name="maxParticipants"
            placeholder="20"
            value={formData?.maxParticipants}
            onChange={onChange}
            required
            min="1"
            description="Güvenli bir deneyim için uygun sayı belirleyin"
          />
          
          <Input
            label="Minimum Katılımcı"
            type="number"
            name="minParticipants"
            placeholder="5"
            value={formData?.minParticipants}
            onChange={onChange}
            required
            min="1"
            description="Etkinliğin gerçekleşmesi için gereken minimum sayı"
          />
        </div>
        
        <div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">
              Kayıt Son Tarihi <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="GG.AA.YYYY"
              value={displayValue}
              onChange={handleDeadlineInput}
              maxLength={10}
              className={`w-40 border rounded-lg px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                errors?.registrationDeadline ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors?.registrationDeadline ? (
              <p className="text-sm text-red-500 mt-1">{errors?.registrationDeadline}</p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                {formData?.date ? `Önerilen: ${calculateDeadline()}` : 'Etkinlik tarihinden önce olmalı'}
              </p>
            )}
          </div>
        </div>
        
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-5 lg:p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-accent flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm md:text-base font-medium text-foreground mb-2">
                Kapasite Yönetimi
              </h3>
              <ul className="space-y-1 text-xs md:text-sm text-muted-foreground">
                <li>• Minimum katılımcı sayısına ulaşılmazsa etkinlik iptal edilir</li>
                <li>• Maksimum kapasiteye ulaşıldığında kayıtlar otomatik kapanır</li>
                <li>• Bekleme listesi özelliği otomatik aktif olur</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div>
          <label className="block text-sm md:text-base font-medium text-foreground mb-3 md:mb-4">
            Kayıt Ayarları
          </label>
          <div className="space-y-3 md:space-y-4">
            <Checkbox
              label="Bekleme Listesi Aktif"
              description="Kapasite dolduğunda katılımcılar bekleme listesine eklenebilir"
              checked={formData?.waitingList}
              onChange={(e) => onCheckboxChange('waitingList', e?.target?.checked)}
            />
            
            <Checkbox
              label="Otomatik Onay"
              description="Kayıtlar manuel onay beklemeden otomatik onaylanır"
              checked={formData?.autoApprove}
              onChange={(e) => onCheckboxChange('autoApprove', e?.target?.checked)}
            />
            
            <Checkbox
              label="Kayıt Hatırlatıcıları Gönder"
              description="Katılımcılara etkinlik öncesi otomatik hatırlatma e-postaları gönder"
              checked={formData?.sendReminders}
              onChange={(e) => onCheckboxChange('sendReminders', e?.target?.checked)}
            />
          </div>
        </div>
        
        <div className="border-t border-border pt-4 md:pt-5 lg:pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="text-center p-3 md:p-4 bg-muted rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
                {formData?.maxParticipants || 0}
              </div>
              <div className="text-xs md:text-sm text-muted-foreground">Maks. Kapasite</div>
            </div>
            
            <div className="text-center p-3 md:p-4 bg-muted rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-secondary mb-1">
                {formData?.minParticipants || 0}
              </div>
              <div className="text-xs md:text-sm text-muted-foreground">Min. Katılımcı</div>
            </div>
            
            <div className="text-center p-3 md:p-4 bg-muted rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-success mb-1">0</div>
              <div className="text-xs md:text-sm text-muted-foreground">Kayıtlı</div>
            </div>
            
            <div className="text-center p-3 md:p-4 bg-muted rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-warning mb-1">
                {formData?.maxParticipants || 0}
              </div>
              <div className="text-xs md:text-sm text-muted-foreground">Kalan Yer</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapacitySection;